import type { GoldSaleOrder } from '../api/types';
import type { AppState, CashoutIntentPayment, CashoutSort } from '../state/store';
import { btn, esc, pill } from './components';
import { formatExpiresIn, formatLocalDateTime, formatTokenAmount } from './format';
import { iconRow } from './icons';

/**
 * Gold Exchange / Cash-out Desk (Phase H).
 *
 * Buying uses buyer-specific purchase reservations (audit1 PAY-1): "Pay with
 * wallet" reserves the order for THIS buyer and pays from the reservation's
 * own payment instructions — the shared order never exposes payment routing.
 * A sent payment is remembered (state + localStorage) and settlement retries
 * from the "Pending Gold purchases" card; Phantom is never reopened for a
 * reservation that already has a signature.
 */

/**
 * Display rate: Gold per 1 whole $OBOL. Pure Gold↔token exchange rate (the
 * $OBOL/USD price is deliberately NOT involved). Needs mint decimals to scale
 * the raw total; returns null when decimals are unknown or inputs are unusable.
 */
export function goldPerObol(order: Pick<GoldSaleOrder, 'goldAmount' | 'obolAmountTotal'>, decimals: number | null): number | null {
  if (decimals === null || !Number.isInteger(decimals) || decimals < 0) return null;
  if (!Number.isFinite(order.goldAmount) || !Number.isFinite(order.obolAmountTotal) || order.obolAmountTotal <= 0) return null;
  return order.goldAmount / (order.obolAmountTotal / 10 ** decimals);
}

export function formatRate(rate: number | null): string {
  if (rate === null || !Number.isFinite(rate)) return 'rate unavailable';
  const digits = rate >= 100 ? 0 : rate >= 1 ? 2 : 6;
  return `${rate.toLocaleString(undefined, { maximumFractionDigits: digits })} Gold / $OBOL`;
}

/**
 * Stable, predictable order sorting. Best rate = highest Gold per $OBOL first
 * (raw ratio — decimals cancel out, so sorting never needs them). Ties and
 * missing data fall back to newest-first, then id for total stability.
 */
export function sortCashoutOrders(orders: readonly GoldSaleOrder[], sort: CashoutSort): GoldSaleOrder[] {
  const createdMs = (o: GoldSaleOrder) => Date.parse(o.createdAt) || 0;
  const rawRate = (o: GoldSaleOrder) => (o.obolAmountTotal > 0 ? o.goldAmount / o.obolAmountTotal : Number.NEGATIVE_INFINITY);
  return [...orders].sort((a, b) => {
    let cmp = 0;
    if (sort === 'rate') cmp = rawRate(b) - rawRate(a);
    else if (sort === 'gold') cmp = b.goldAmount - a.goldAmount;
    if (cmp === 0) cmp = createdMs(b) - createdMs(a);
    if (cmp === 0) cmp = a.id.localeCompare(b.id);
    return cmp;
  });
}

/** Another buyer's unexpired reservation blocks this order for everyone else. */
export function isReservedByOther(order: Pick<GoldSaleOrder, 'reservedUntil'>, state: AppState): boolean {
  if (!order.reservedUntil || Date.parse(order.reservedUntil) <= Date.now()) return false;
  return !(state.myCashoutIntents ?? []).some((i) => i.status === 'active' && i.expiresAt === order.reservedUntil);
}

/** The caller's own active intent on this order that already has a wallet-sent signature. */
function sentPaymentForOrder(orderId: string, state: AppState): { intentId: string } | null {
  const intent = (state.myCashoutIntents ?? []).find((i) => i.status === 'active' && i.goldSaleOrderId === orderId);
  if (!intent || !state.cashoutIntentPayments[intent.id]) return null;
  return { intentId: intent.id };
}

function headerCard(state: AppState): string {
  return `
    <section class="card card--wide market-head">
      <div class="run__head">
        <h2><span class="ember-dot" aria-hidden="true"></span> Gold Exchange</h2>
        <div class="btn-row">
          ${btn('Refresh', 'cashout-refresh', {}, { variant: 'ghost' })}
          ${btn('Back to Waystation', 'goto-waystation', {}, { variant: 'ghost' })}
        </div>
      </div>
      <p class="muted">Sell banked Gold for buyer-paid $OBOL. <strong>95% seller</strong> / <strong>5% treasury</strong>; the server only verifies payment and moves internal Gold. Buying reserves the order for you first — pay from your reservation before it expires.</p>
      ${iconRow('Your Gold', 'gold', `<strong>${esc(state.cashoutGoldBalance ?? 0)}</strong>`)}
    </section>`;
}

function createOrderCard(state: AppState): string {
  const decimals = state.cashoutTokenDecimals;
  const decimalsHint = decimals !== null && decimals > 0
    ? `Prices accept up to ${decimals} decimal places; whole $OBOL amounts always split cleanly.`
    : 'Whole $OBOL amounts always split cleanly into the 95/5 payout.';
  return `
    <section class="card card--wide">
      <h2>Create Gold sale order</h2>
      <p class="muted">Listing locks your Gold until payment verifies, the listing expires, or you cancel. ${esc(decimalsHint)}</p>
      <div class="cashout-form">
        <label>Gold <input class="market-price" type="number" min="1" step="1" data-cashout-gold placeholder="Gold amount" /></label>
        <label>Total $OBOL price <input class="market-price" type="text" inputmode="decimal" data-cashout-obol placeholder="e.g. 10" aria-label="Total $OBOL price (whole tokens)" /></label>
        <label>Listing duration (days) <input class="market-price" type="number" min="1" max="30" step="1" value="7" data-cashout-days aria-label="Listing duration in days (1 to 30)" /></label>
        ${btn('List Gold', 'cashout-create', {}, { variant: 'primary' })}
      </div>
    </section>`;
}

function statusPill(status: GoldSaleOrder['status']): string {
  if (status === 'active') return pill('active', 'ok');
  if (status === 'paid') return pill('paid', 'muted');
  if (status === 'cancelled') return pill('cancelled', 'down');
  return pill('expired', 'warn');
}

function expiresLine(order: Pick<GoldSaleOrder, 'expiresAt'>): string {
  return `<span class="cashout-expires" data-expires-countdown data-expires-at="${esc(order.expiresAt)}" title="${esc(formatLocalDateTime(order.expiresAt))} (local time)" role="timer">${esc(formatExpiresIn(order.expiresAt))}</span>`;
}

/** Buy action for someone else's active order: reservation-aware, no raw payment routing. */
function orderAction(order: GoldSaleOrder, state: AppState): string {
  if (isReservedByOther(order, state)) {
    return `<div class="btn-row">${pill('reserved by another buyer', 'warn')}</div>`;
  }
  if (sentPaymentForOrder(order.id, state)) {
    return `<div class="btn-row">${pill('payment sent — see Pending Gold purchases above', 'warn')}</div>`;
  }
  return `<div class="btn-row">${btn('Pay with wallet', 'cashout-pay', { order: order.id }, { variant: 'primary' })}</div>`;
}

function orderCard(order: GoldSaleOrder, state: AppState): string {
  const own = order.sellerAccountId === state.account?.account.id;
  const decimals = state.cashoutTokenDecimals;
  const rate = goldPerObol(order, decimals);
  const price = `${formatTokenAmount(order.obolAmountTotal, decimals)} $OBOL`;
  const action = own
    ? `<div class="btn-row">${pill('your order — cannot buy', 'muted')}</div>`
    : orderAction(order, state);
  return `
    <div class="item cashout-order">
      <div class="item__head">
        <strong>${esc(order.goldAmount)} Gold</strong>
        <span class="pill pill--ok">${esc(price)}</span>
        <span class="pill pill--muted cashout-rate">${esc(formatRate(rate))}</span>
      </div>
      <div class="item__meta">seller <code>${esc(order.sellerLabel)}</code> · ${expiresLine(order)}</div>
      ${action}
    </div>`;
}

/** Status + actions for a reservation whose wallet payment was already sent. */
function sentPaymentRow(intentId: string, payment: CashoutIntentPayment): string {
  const statusText = payment.status === 'confirming'
    ? pill('Payment sent — waiting for confirmation…', 'warn')
    : pill('Payment sent but not confirmed yet', 'warn');
  const retry = btn('Retry settlement', 'cashout-intent-retry', { intent: intentId }, { variant: 'primary', disabled: payment.status === 'confirming' });
  return `
        <div class="item__meta">${statusText} signature <code>${esc(payment.signature.slice(0, 8))}…</code></div>
        <div class="btn-row">
          ${retry}
          <input class="market-price" type="text" data-cashout-tx-for="${esc(intentId)}" placeholder="Paste tx signature (fallback)" aria-label="Transaction signature" />
          ${btn('Submit signature', 'cashout-intent-submit', { intent: intentId }, { variant: 'ghost' })}
        </div>`;
}

/**
 * The buyer's reserved Gold Exchange purchases + any stored-but-unsettled
 * payment signatures (audit1 PAY-2): a payment sent near the deadline keeps
 * its recovery path here — never a second wallet payment.
 */
export function pendingGoldPurchasesCard(state: AppState): string {
  const intents = (state.myCashoutIntents ?? []).filter(
    (i) => i.status === 'active' && Date.parse(i.expiresAt) > Date.now(),
  );
  const payments = state.cashoutIntentPayments;
  const orphans = Object.entries(payments).filter(([intentId]) => !intents.some((i) => i.id === intentId));
  if (intents.length === 0 && orphans.length === 0) return '';
  const decimals = state.cashoutTokenDecimals;
  const cards = intents.map((i) => {
    const payment = payments[i.id];
    const mock = i.paymentInstructions?.devMockSignature
      ? btn('Submit dev mock payment', 'cashout-intent-submit-mock', { intent: i.id, tx: i.paymentInstructions.devMockSignature }, { variant: 'ghost' })
      : '';
    // Once a signature exists for this reservation the wallet must NOT be
    // reopened — settlement retries reuse the stored signature.
    const actions = payment
      ? sentPaymentRow(i.id, payment)
      : `
        <div class="btn-row">
          ${btn(`Pay with wallet · ${formatTokenAmount(i.obolAmountTotal, decimals)} $OBOL`, 'cashout-pay', { order: i.goldSaleOrderId }, { variant: 'primary' })}
          <input class="market-price" type="text" data-cashout-tx-for="${esc(i.id)}" placeholder="Paste tx signature" aria-label="Transaction signature" />
          ${btn('Submit signature', 'cashout-intent-submit', { intent: i.id }, { variant: 'ghost' })}
          ${mock}
        </div>`;
    return `
      <div class="item">
        <div class="item__head">
          <span>${esc(i.goldAmount)} Gold purchase</span>
          ${pill(`${formatTokenAmount(i.obolAmountTotal, decimals)} $OBOL`, 'warn')}
          <span class="muted market-fee-copy">95/5 split</span>
        </div>
        <div class="item__meta">Reserved for you until <span title="${esc(i.expiresAt)}">${esc(new Date(i.expiresAt).toLocaleTimeString())}</span> — pay before it expires.</div>
        ${actions}
      </div>`;
  }).join('');
  const orphanCards = orphans.map(([intentId, payment]) => `
      <div class="item">
        <div class="item__head">
          <span>Gold purchase — unconfirmed payment</span>
        </div>
        ${sentPaymentRow(intentId, payment)}
        <div class="btn-row">${btn('Discard stored signature', 'cashout-intent-dismiss', { intent: intentId }, { variant: 'ghost' })}</div>
      </div>`).join('');
  return `
    <section class="card card--wide">
      <h2>Pending Gold purchases</h2>
      <p class="muted">Your reserved Gold orders awaiting wallet payment. The server verifies the on-chain payment before the Gold transfers.</p>
      <div class="items">${cards}${orphanCards}</div>
    </section>`;
}

function sortSelect(current: CashoutSort): string {
  const options: Array<[CashoutSort, string]> = [
    ['rate', 'Best rate'],
    ['gold', 'Most Gold'],
    ['newest', 'Newest'],
  ];
  return `
    <label class="cashout-sort">Sort
      <select data-cashout-sort aria-label="Sort Gold sale orders">
        ${options.map(([value, label]) => `<option value="${esc(value)}"${value === current ? ' selected' : ''}>${esc(label)}</option>`).join('')}
      </select>
    </label>`;
}

function activeOrdersCard(state: AppState): string {
  const orders = sortCashoutOrders(state.cashoutOrders ?? [], state.cashoutSort);
  const cards = orders.map((o) => orderCard(o, state)).join('');
  const body = orders.length > 0 ? `<div class="items">${cards}</div>` : '<p class="muted">No active Gold sale orders right now.</p>';
  return `
    <section class="card card--wide">
      <div class="run__head">
        <h2>Active Gold orders</h2>
        <div class="btn-row">${sortSelect(state.cashoutSort)}${btn('Refresh', 'cashout-refresh', {}, { variant: 'ghost' })}</div>
      </div>
      ${body}
    </section>`;
}

/**
 * Active listings only. The backend already limits /cashout/my-orders to the
 * caller's open orders; the timestamp guard here drops orders whose expiry
 * passed since the last fetch (expiry is settled lazily server-side).
 */
export function activeMyCashoutOrders(orders: readonly GoldSaleOrder[], now: number = Date.now()): GoldSaleOrder[] {
  return orders.filter((o) => o.status === 'active' && (Date.parse(o.expiresAt) || 0) > now);
}

function myOrdersCard(state: AppState): string {
  const orders = activeMyCashoutOrders(state.myCashoutOrders ?? []);
  const decimals = state.cashoutTokenDecimals;
  const cards = orders
    .map((o) => `
        <div class="item cashout-order">
          <div class="item__head">
            <strong>${esc(o.goldAmount)} Gold</strong>
            <span class="pill pill--ok">${esc(formatTokenAmount(o.obolAmountTotal, decimals))} $OBOL</span>
            <span class="pill pill--muted cashout-rate">${esc(formatRate(goldPerObol(o, decimals)))}</span>
            ${statusPill(o.status)}
            ${o.reservedUntil && Date.parse(o.reservedUntil) > Date.now() ? pill('reserved by a buyer', 'warn') : ''}
          </div>
          <div class="item__meta">seller ${esc(formatTokenAmount(o.sellerObolAmount, decimals))} · treasury ${esc(formatTokenAmount(o.treasuryObolAmount, decimals))} $OBOL · ${expiresLine(o)}</div>
          <div class="btn-row">${btn('Cancel order', 'cashout-cancel', { order: o.id }, { variant: 'ghost' })}</div>
        </div>`)
    .join('');
  const body = orders.length > 0 ? `<div class="items">${cards}</div>` : '<p class="muted">You have no active Gold sale orders.</p>';
  return `<section class="card card--wide"><h2>My Gold orders</h2>${body}</section>`;
}

export function renderCashout(state: AppState): string {
  if (!state.session) {
    return `<section class="card"><h2>Gold Exchange</h2><p class="muted">Connect a wallet to buy or sell Gold for $OBOL.</p></section>`;
  }
  return `
    <div class="grid">
      ${headerCard(state)}
      ${pendingGoldPurchasesCard(state)}
      ${createOrderCard(state)}
      ${activeOrdersCard(state)}
      ${myOrdersCard(state)}
    </div>`;
}
